import { z } from 'zod';
import { OPEN_WORLD_MAX_EXPEDITION_MEMBERS } from './expedition';
import { openWorldBoundsSchema, openWorldCoordinateSchema } from './grid';
import { OPEN_WORLD_PARTY_MEMBER_STATES, openWorldPartyNameSchema } from './party';
import { OPEN_WORLD_SAFE_PORT_IDS } from './ports';

/**
 * **O mapa dos cem quadrantes: a grelha é conhecida, o conteúdo não** (V28I.R).
 *
 * ## As duas coisas que este ficheiro mantém separadas
 *
 * A **grelha** — dez linhas, dez colunas, oito portos — é geografia pública. Ela
 * é a mesma para toda a gente, deriva de constantes que já existem, e escondê-la
 * não protegia nada: um jogador que voa aprende-a em vinte minutos, e escondê-la
 * só tornava impossível combinar um ponto de encontro.
 *
 * O **catálogo** — perigo, riqueza, recursos, água, planetas, cometas, mobs,
 * bosses, tempos de renascimento — é segredo, e continua a sê-lo. **Nada dele
 * tem campo aqui.** Não é escondido no CSS, não é filtrado na interface e não
 * viaja "para o caso": não existe um sítio nesta forma onde ele coubesse.
 *
 * É por isso que visitar um quadrante revela **apenas que ele foi visitado**. A
 * descoberta é um facto sobre o piloto, e nunca um facto sobre o quadrante.
 *
 * ## O que o navegador não diz
 *
 * A conta, o mundo, a coordenada atual e a lista de descobertas. Nenhuma tem
 * campo em nenhum pedido — a leitura não tem corpo nenhum — e é essa ausência,
 * e não uma verificação, que impede alguém de ler o mapa de outra pessoa ou de
 * inventar que esteve num sítio onde nunca esteve.
 */

/**
 * A versão da forma do mapa, no fio.
 *
 * Sobe quando a resposta passa a dizer mais. **Nunca** quando passa a dizer
 * mais sobre o CONTEÚDO de um quadrante: essa mudança não é uma versão, é uma
 * decisão de desenho que ainda não foi tomada.
 */
export const OPEN_WORLD_MAP_VERSION = 1;

/** Uma leitura, e não tem corpo: tudo o que ela precisa vem da sessão. */
export const OPEN_WORLD_MAP_ROUTE = '/api/open-world/map';

/**
 * **Um aliado, como o mapa o desenha.**
 *
 * O quadrante e o estado, que são exatamente os que a vista privada da PT já
 * publica desde o V28I.Q.1 — e nada de novo. Nunca uma posição dentro do
 * quadrante, nunca vida, nunca carga: um mapa não é uma forma de ver através de
 * paredes, e um marcador numa célula diz "para ali", não "ali".
 */
export const openWorldMapAllySchema = z
  .object({
    /** O id público da conta, que os integrantes da composição já conhecem. */
    id: z.uuid(),
    name: openWorldPartyNameSchema,
    /** É o líder da expedição. */
    leader: z.boolean(),
    /** Em que quadrante ele está. Presente só quando ele está em algum. */
    quadrant: openWorldCoordinateSchema,
    /** O mesmo estado escrito da vista da PT, para o mapa não inventar um segundo. */
    state: z.enum(OPEN_WORLD_PARTY_MEMBER_STATES),
  })
  .strict();

export type OpenWorldMapAlly = z.infer<typeof openWorldMapAllySchema>;

/**
 * **Um porto, como o mapa o recebe.**
 *
 * Derivado de `OPEN_WORLD_SAFE_PORTS` no servidor e nunca escrito outra vez:
 * uma segunda tabela com `A/3`, `A/8`, `J/3`, `J/8`, `C/1`, `H/1`, `C/10` e
 * `H/10` seria oito números a envelhecer em silêncio no dia em que um porto
 * mudasse de sítio.
 */
export const openWorldMapPortSchema = z
  .object({ id: z.enum(OPEN_WORLD_SAFE_PORT_IDS), quadrant: openWorldCoordinateSchema })
  .strict();

export type OpenWorldMapPort = z.infer<typeof openWorldMapPortSchema>;

/**
 * **A revisão de uma resposta: o mundo e a época.**
 *
 * Existe para uma corrida concreta. Duas leituras podem estar no ar ao mesmo
 * tempo — a que abriu o mapa e a que uma travessia disparou — e a rede não
 * promete que chegam pela ordem em que saíram.
 *
 * A **época** é a do coordenador desta conta: monótona, e sobe exatamente nas
 * escritas que mudam onde ela está — a linearização de uma travessia e a
 * libertação de uma extração. Uma resposta com época menor descreve um passado.
 *
 * O **mundo** está aqui porque a época é monótona DENTRO de um mundo e não
 * entre mundos: um coordenador é endereçado por mundo e conta, portanto um
 * mundo novo começa a contar do zero. Sem esta metade, a resposta de um mundo
 * anterior podia parecer mais recente. Não é uma revelação nova — a identidade
 * do mundo ativo já viaja no estado de entrada.
 */
export const openWorldMapRevisionSchema = z
  .object({
    world: z.string().min(1).max(128),
    epoch: z.number().int().min(0),
  })
  .strict();

export type OpenWorldMapRevision = z.infer<typeof openWorldMapRevisionSchema>;

/**
 * **O mapa desta conta, e de mais ninguém.**
 *
 * Sete campos, e a lista é exaustiva de propósito: a forma é a promessa. Um
 * campo de perigo, de riqueza ou de recursos teria de ser acrescentado aqui
 * para poder existir, e acrescentá-lo seria uma decisão visível em vez de um
 * acidente.
 *
 * **O tamanho da resposta não diz nada sobre o mundo.** Os limites são fixos, os
 * portos são oito, os aliados são no máximo nove, e as descobertas dependem só
 * de onde este piloto já esteve. Nada aqui cresce com a riqueza de um quadrante,
 * porque nada aqui a lê.
 */
export const openWorldMapViewSchema = z
  .object({
    v: z.literal(OPEN_WORLD_MAP_VERSION),
    /** Os limites ATIVOS, como o servidor os afirma. O cliente não os assume. */
    bounds: openWorldBoundsSchema,
    /** Onde esta conta está agora, ou nada quando está fora do mundo. */
    current: openWorldCoordinateSchema.nullable(),
    /**
     * **Os quadrantes onde esta conta já esteve**, neste mundo.
     *
     * Um conjunto de coordenadas e mais nada: nem quando, nem quantas vezes,
     * nem o que lá encontrou. "Visitado" é a informação inteira.
     */
    discovered: z.array(openWorldCoordinateSchema),
    /** Os oito, sempre conhecidos, mesmo antes de visitados. */
    ports: z.array(openWorldMapPortSchema),
    /** Os aliados da expedição ATIVA que estão em algum quadrante. */
    allies: z.array(openWorldMapAllySchema).max(OPEN_WORLD_MAX_EXPEDITION_MEMBERS),
    revision: openWorldMapRevisionSchema,
  })
  .strict();

export type OpenWorldMapView = z.infer<typeof openWorldMapViewSchema>;

/**
 * **O estado de uma célula, para quem a desenha.**
 *
 * Quatro palavras fechadas. `current` ganha a `port`, e `port` ganha a
 * `visited`: uma célula tem um estado, e uma interface que somasse dois teria
 * de escolher qual mostrar em cada sítio onde a desenha.
 */
export const OPEN_WORLD_MAP_CELL_STATES = ['current', 'port', 'visited', 'unvisited'] as const;

export type OpenWorldMapCellState = (typeof OPEN_WORLD_MAP_CELL_STATES)[number];
